import keys from "lodash/keys";
import size from "lodash/size";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import IccidPukHint from "@/assets/onboarding/sim-verification/IccidAndPuk.svg?react";
import { EsimInfoModal } from "@/components/Checkout/Steps/SimSelection/EsimInfoModal/EsimInfoModal";
import { GoToShopSection } from "@/components/Checkout/Steps/SimSelection/GoToShopSection";
import { PhysicalSimFields } from "@/components/Checkout/Steps/SimSelection/PhysicalSimFields/PhysicalSimFields";
import { useSimVerificationFlow } from "@/components/Checkout/Steps/SimVerification/useSimVerificationFlow";
import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useAddBasketEntry,
  useGetSimCustomerActions,
  useRemoveBasketEntry,
  useSaveBasket,
} from "@/services/api";
import { Basket } from "@/services/model";
import { BASE_OPTION_PRODUCT_ID } from "@/views/Portal/BulkSimActivation/bulkSimActivationUtils";

/**
 * Combined Step 1: physical SIM ICCID/PUK entry, eSIM shortcut (modal), and shop CTA.
 * Rendered inside the flow's StepFlowLayout, which owns the headline and stepper.
 **/
export const SimSelection: FC = () => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showEsimModal, setShowEsimModal] = useState(false);
  const [isPreparingEsim, setIsPreparingEsim] = useState(false);

  const { data: simCustomerInfo } = useGetSimCustomerActions();

  const isLockedPhysicalSim =
    dataContext.wizardEntry?.lockedSimType === "physical";
  const isPhysicalPurchaseIntent =
    dataContext.wizardEntry?.source === "purchase-intent" &&
    isLockedPhysicalSim;

  const canAddESim =
    !isLockedPhysicalSim && (simCustomerInfo?.addESIMAllowed ?? true);
  const canAddSim = simCustomerInfo?.addTripleSIMAllowed ?? true;
  const canEnterPhysicalSim = canAddSim || isPhysicalPurchaseIntent;

  const { mutate: getBasicBasket, isLoading: isSavingBasket } = useSaveBasket({
    mutation: {
      onSuccess: (data) => {
        setDataContext((prev) => ({
          ...prev,
          basket: data,
        }));
      },
    },
  });

  const { mutateAsync: addBasketEntry, isLoading: isAddingBasketEntry } =
    useAddBasketEntry({
      mutation: {
        onSuccess: (data) => {
          setDataContext((prev) => ({
            ...prev,
            basket: data,
          }));
        },
      },
    });

  const { mutateAsync: removeBasketEntry, isLoading: isRemovingBasketEntry } =
    useRemoveBasketEntry({
      mutation: {
        onSuccess: (data) => {
          setDataContext((prev) => ({
            ...prev,
            basket: data,
          }));
        },
      },
    });

  const {
    register,
    setValue,
    errors,
    handleSubmit,
    pukMandatory,
    sim,
    isLoadingCheckSim,
    continueIsEnabled,
    isRoutingToNextStep,
    showOverlay,
    setShowOverlay,
    saveAndNavigateForward,
  } = useSimVerificationFlow();

  useEffect(() => {
    if (!dataContext.basket) {
      getBasicBasket({});
    }
  }, [dataContext.basket, getBasicBasket]);

  /**
   * Wipe leftover entries and add a fresh base product with the given SIM type.
   * Returns the updated basket so callers can chain setICCID without stale context.
   */
  const ensureBaseBasketEntry = async ({
    isESIM,
  }: {
    isESIM: boolean;
  }): Promise<Basket | undefined> => {
    const basketId = dataContext.basket?.id;

    if (!basketId) {
      return undefined;
    }

    const entries = dataContext.basket?.entries;

    if (size(entries) > 0) {
      const indexes = keys(entries);

      for (const index of indexes) {
        await removeBasketEntry({
          index: Number.parseInt(index),
          basketId,
        });
      }
    }

    const basket = await addBasketEntry({
      basketId,
      productId: BASE_OPTION_PRODUCT_ID,
      params: { isESIM },
    });

    setDataContext((prev) => ({
      ...prev,
      basket,
      allowedMaxOnboardingStep: 2,
    }));

    return basket;
  };

  const handlePhysicalSubmit = handleSubmit(async () => {
    if (!canEnterPhysicalSim) {
      return;
    }

    // Purchase intents already have a prepared basket (and tariff). Wiping it
    // would drop the ordered product before ICCID is written.
    const basket = isPhysicalPurchaseIntent
      ? dataContext.basket
      : await ensureBaseBasketEntry({ isESIM: false });
    saveAndNavigateForward(basket);
  });

  const handleEsimConfirm = async () => {
    if (!canAddESim) {
      return;
    }

    setIsPreparingEsim(true);
    try {
      await ensureBaseBasketEntry({ isESIM: true });
      setShowEsimModal(false);
      navigate(portalRoutes.addDevice.rateSelection);
    } finally {
      setIsPreparingEsim(false);
    }
  };

  const isBasketReady = !!dataContext.basket?.id;
  const isLoading =
    !isBasketReady ||
    isAddingBasketEntry ||
    isRemovingBasketEntry ||
    isSavingBasket ||
    isRoutingToNextStep ||
    isPreparingEsim;

  return (
    <div
      data-testid="onboarding-select-sim"
      className="w-full"
      id="onboarding-select-sim"
    >
      {(isLoading || isLoadingCheckSim) && <LoadingSpinner />}

      <form
        id="add-device"
        className="flex w-full flex-col gap-6"
        onSubmit={(event) => {
          void handlePhysicalSubmit(event);
        }}
      >
        <div className="flex flex-col gap-6 tablet:grid tablet:grid-cols-2 tablet:items-start">
          <div className="hidden overflow-hidden rounded-lg bg-background-primary p-4 tablet:block">
            <IccidPukHint className="h-auto w-full" />
          </div>

          <div className="flex flex-col gap-4">
            <PhysicalSimFields
              register={register}
              errors={errors}
              setValue={setValue}
              pukMandatory={pukMandatory}
              simUnavailable={sim?.message === "Sim is not available"}
              showOverlay={showOverlay}
              setShowOverlay={setShowOverlay}
              disabled={!canEnterPhysicalSim || isLoading}
            />

            {canAddESim && (
              <Link
                className="self-start"
                data-testid="esim-generate-link"
                disabled={isLoading}
                type="button"
                onClick={() => setShowEsimModal(true)}
              >
                {t("portal:add-device.simSelection.generateEsimLink")}
              </Link>
            )}
          </div>
        </div>

        <GoToShopSection />

        <StepFlowNavigation
          primaryButton={
            <Button
              variant="primary"
              disabled={!canEnterPhysicalSim || !continueIsEnabled || isLoading}
              type="submit"
            >
              {t("Common.label.forward")}
            </Button>
          }
          secondaryButton={
            // Step 1 has nothing to go back to - the design still shows the
            // button, greyed out, so the button row keeps its shape.
            <Button variant="secondary" disabled={true}>
              {t("Common.label.back")}
            </Button>
          }
        />
      </form>

      {showEsimModal && (
        <EsimInfoModal
          onClose={() => setShowEsimModal(false)}
          onConfirm={handleEsimConfirm}
          isConfirming={isPreparingEsim}
        />
      )}
    </div>
  );
};
